import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Monitor, FolderOpen, ListVideo, Calendar, Menu, LogOut, Film, CreditCard, ChevronDown, Dumbbell, User, Receipt, Plug, Activity } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import BuyCreditsModal from './BuyCreditsModal';
import './Navbar.css';

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { logout, currentUser, isAdmin } = useAuth();

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Real-time listener for user data (includes credit balance)
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      if (snap.exists()) {
        setUserData(snap.data());
      }
    });
    return unsub;
  }, [currentUser]);

  // Get user info from Firestore or fallback to auth
  const userEmail = userData?.email || currentUser?.email || '';
  const userName = userData ? `${userData.firstName} ${userData.lastName}` : userEmail.split('@')[0];
  const initials = userName.split(' ').map(n => n[0]).join('');

  const handleEquipmentProfile = () => {
    setDropdownOpen(false);
    navigate('/equipment-profile');
  };

  const handlePersonalProfile = () => {
    setDropdownOpen(false);
    navigate('/personal-profile');
  };

  const handlePaymentSettings = () => {
    setDropdownOpen(false);
    navigate('/payment-settings');
  };

  const handleSubscription = () => {
    setDropdownOpen(false);
    navigate('/subscription');
  };

  const handleTvPower = () => {
    setDropdownOpen(false);
    navigate('/tv-power');
  };

  const handleLogout = async () => {
    setDropdownOpen(false);
    try {
      // Add fade out
      document.body.style.opacity = '0';
      document.body.style.transition = 'opacity 0.3s ease-out';
      
      // Wait for fade
      await new Promise(resolve => setTimeout(resolve, 300));
      
      await logout();
      navigate('/login');
      
      // Reset opacity
      document.body.style.opacity = '1';
    } catch (error) {
      console.error('Logout error:', error);
      document.body.style.opacity = '1';
    }
  };

  return (
    <>
      {/* Top Navbar */}
      <nav className="navbar">
        {/* Hamburger Button (Mobile Only) */}
        <button className="hamburger-btn" onClick={toggleMenu} aria-label="Open menu">
          <Menu size={24} />
        </button>

        <div className="navbar-brand">
          <img src="/white.svg" alt="Pulse" height="22" />
        </div>
        
        {/* Desktop Navigation */}
        <div className="navbar-menu">
          <NavLink to="/screens" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
            <Monitor size={18} />
            Screens
          </NavLink>
          <NavLink to="/assets" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
            <FolderOpen size={18} />
            Files/Assets
          </NavLink>
          <NavLink to="/playlists" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
            <ListVideo size={18} />
            Playlists
          </NavLink>
          <NavLink to="/schedules" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
            <Calendar size={18} />
            Schedules
          </NavLink>
          <NavLink to="/video-builder" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
  <Film size={18} />
  Video Builder
</NavLink>
          <NavLink to="/programs" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
            <Activity size={18} />
            Programs
          </NavLink>
        </div>

        {/* Desktop User Dropdown */}
        <div className="navbar-user" ref={dropdownRef}>
          <button className="user-dropdown-btn" onClick={toggleDropdown}>
            <span className="user-name">{userName}</span>
            <ChevronDown size={16} className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`} />
          </button>
          
          {dropdownOpen && (
            <div className="user-dropdown-menu">
              <div className="dropdown-header">
                <div className="dropdown-avatar">{initials}</div>
                <div className="dropdown-user-info">
                  <div className="dropdown-user-name">{userName}</div>
                  <div className="dropdown-user-email">{userEmail}</div>
                </div>
              </div>
              
              <div className="dropdown-divider" />
              
              {!isAdmin && (
                <div style={{
                  padding: '8px 16px', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', background: '#fafafa',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Film size={16} color="#dc3545" />
                    <div>
                      <span style={{ fontSize: '14px', color: (userData?.videoCredits || 0) > 0 ? '#1a1a2e' : '#999' }}>
                        {userData?.videoCredits ?? 0} credits
                      </span>
                    </div>
                  </div>
                  <span
                    onClick={(e) => { e.stopPropagation(); setDropdownOpen(false); setShowBuyCreditsModal(true); }}
                    style={{ fontSize: '12px', color: '#dc3545', cursor: 'pointer' }}
                  >
                    Buy credits
                  </span>
                </div>
              )}

              {!isAdmin && (
                <div style={{ padding: '0 16px 4px' }}>
                  <span style={{ fontSize: '11px', color: '#999' }}>
                    For building custom <span
                      onClick={(e) => { e.stopPropagation(); setDropdownOpen(false); navigate('/video-builder'); }}
                      title="Create branded exercise videos with built-in timers using the Video Builder."
                      style={{ color: '#dc3545', cursor: 'pointer', borderBottom: '1px dotted #dc3545' }}
                    >branded videos</span>
                  </span>
                </div>
              )}

              <div className="dropdown-divider" />
              
              <button className="dropdown-item" onClick={handleEquipmentProfile}>
                <Dumbbell size={18} />
                Equipment Profile
              </button>
              
              <button className="dropdown-item" onClick={handlePersonalProfile}>
                <User size={18} />
                Personal Profile
              </button>
              
              <button className="dropdown-item" onClick={handlePaymentSettings}>
                <CreditCard size={18} />
                Payment Settings
              </button>

              <button className="dropdown-item" onClick={handleSubscription}>
                <Receipt size={18} />
                Subscription Plan
              </button>

              <button className="dropdown-item" onClick={handleTvPower}>
                <Plug size={18} />
                Smart Power
              </button>
              
              <div className="dropdown-divider" />
              
              <button className="dropdown-item logout" onClick={handleLogout}>
                <LogOut size={18} />
                Log Out
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Slide-out Menu Overlay (Mobile) */}
      <div 
        className={`slide-menu-overlay ${menuOpen ? 'open' : ''}`} 
        onClick={closeMenu}
      />

      {/* Slide-out Menu (Mobile) */}
      <div className={`slide-menu ${menuOpen ? 'open' : ''}`}>
        <div className="slide-menu-header">
          <div className="slide-menu-user">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <div className="user-info-name">{userName}</div>
              <div className="user-info-email">{userEmail}</div>
            </div>
          </div>
        </div>

        {!isAdmin && (
          <div style={{
            padding: '10px 20px', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', background: '#fafafa',
            borderBottom: '1px solid #eee',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Film size={18} color="#dc3545" />
              <span style={{ fontSize: '14px', color: (userData?.videoCredits || 0) > 0 ? '#1a1a2e' : '#999' }}>
                {userData?.videoCredits ?? 0} credits
              </span>
            </div>
            <span
              onClick={() => { closeMenu(); setShowBuyCreditsModal(true); }}
              style={{ fontSize: '12px', color: '#dc3545', cursor: 'pointer' }}
            >
              Buy credits
            </span>
          </div>
        )}

        <nav className="slide-menu-nav">
          <NavLink 
            to="/screens" 
            className={({ isActive }) => isActive ? 'slide-menu-link active' : 'slide-menu-link'}
            onClick={closeMenu}
          >
            <Monitor size={20} />
            Screens
          </NavLink>
          <NavLink 
            to="/assets" 
            className={({ isActive }) => isActive ? 'slide-menu-link active' : 'slide-menu-link'}
            onClick={closeMenu}
          >
            <FolderOpen size={20} />
            Media Library
          </NavLink>
          <NavLink 
            to="/playlists" 
            className={({ isActive }) => isActive ? 'slide-menu-link active' : 'slide-menu-link'}
            onClick={closeMenu}
          >
            <ListVideo size={20} />
            Playlists
          </NavLink>
          <NavLink 
            to="/schedules" 
            className={({ isActive }) => isActive ? 'slide-menu-link active' : 'slide-menu-link'}
            onClick={closeMenu}
          >
            <Calendar size={20} />
            Schedules
          </NavLink>
          <NavLink 
  to="/video-builder" 
  className={({ isActive }) => isActive ? 'slide-menu-link active' : 'slide-menu-link'}
  onClick={closeMenu}
>
  <Film size={20} />
  Video Builder
</NavLink>
          <NavLink
            to="/programs"
            className={({ isActive }) => isActive ? 'slide-menu-link active' : 'slide-menu-link'}
            onClick={closeMenu}
          >
            <Activity size={20} />
            Programs
          </NavLink>

          <div className="slide-menu-divider" />
          
          <div className="slide-menu-section-title">Account</div>
          
          <NavLink 
            to="/equipment-profile" 
            className={({ isActive }) => isActive ? 'slide-menu-link active' : 'slide-menu-link'}
            onClick={closeMenu}
          >
            <Dumbbell size={20} />
            Equipment Profile
          </NavLink>
          
          <NavLink 
            to="/personal-profile" 
            className={({ isActive }) => isActive ? 'slide-menu-link active' : 'slide-menu-link'}
            onClick={closeMenu}
          >
            <User size={20} />
            Personal Profile
          </NavLink>

          <NavLink 
            to="/payment-settings" 
            className={({ isActive }) => isActive ? 'slide-menu-link active' : 'slide-menu-link'}
            onClick={closeMenu}
          >
            <CreditCard size={20} />
            Payment Settings
          </NavLink>

          <NavLink 
            to="/subscription" 
            className={({ isActive }) => isActive ? 'slide-menu-link active' : 'slide-menu-link'}
            onClick={closeMenu}
          >
            <Receipt size={20} />
            Subscription Plan
          </NavLink>

          <NavLink 
            to="/tv-power" 
            className={({ isActive }) => isActive ? 'slide-menu-link active' : 'slide-menu-link'}
            onClick={closeMenu}
          >
            <Plug size={20} />
            Smart Power
          </NavLink>
        </nav>

        <div className="slide-menu-footer">
          <button className="logout-btn" onClick={() => { handleLogout(); closeMenu(); }}>
            <LogOut size={20} />
            Log out
          </button>
        </div>
      </div>

      {/* Bottom Navigation (Mobile) */}
      <nav className="bottom-nav">
        <div className="bottom-nav-container">
          <NavLink 
            to="/screens" 
            className={({ isActive }) => isActive ? 'bottom-nav-link active' : 'bottom-nav-link'}
          >
            <Monitor size={22} />
            <span>Screens</span>
          </NavLink>
          <NavLink 
            to="/assets" 
            className={({ isActive }) => isActive ? 'bottom-nav-link active' : 'bottom-nav-link'}
          >
            <FolderOpen size={22} />
            <span>Files</span>
          </NavLink>
          <NavLink 
            to="/playlists" 
            className={({ isActive }) => isActive ? 'bottom-nav-link active' : 'bottom-nav-link'}
          >
            <ListVideo size={22} />
            <span>Playlists</span>
          </NavLink>
          <NavLink 
            to="/schedules" 
            className={({ isActive }) => isActive ? 'bottom-nav-link active' : 'bottom-nav-link'}
          >
            <Calendar size={22} />
            <span>Schedules</span>
          </NavLink>
        </div>
      </nav>

      <BuyCreditsModal
        isOpen={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
        currentBalance={userData?.videoCredits ?? 0}
        cardLast4={userData?.savedCard?.last4 || ''}
        onPurchaseComplete={() => {}}
      />
    </>
  );
}

export default Navbar;